/**
 * @license Copyright (c) 2003-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import ClickObserver from '@ckeditor/ckeditor5-engine/src/view/observer/clickobserver';
import { ContextualBalloon, clickOutsideHandler } from '@ckeditor/ckeditor5-ui';
import FormView from './linklawview';
import '../styles.css';
import getRangeText from './utils.js';
import findAttributeRange from '@ckeditor/ckeditor5-typing/src/utils/findattributerange'; 


export default class LinklawUI extends Plugin {
	static get requires() {
		return [ ContextualBalloon ];
	}

	init() {
		const editor = this.editor;

        // Create the balloon and the form view.
		this._balloon = this.editor.plugins.get( ContextualBalloon );
		this.formView = this._createFormView();

		editor.ui.componentFactory.add( 'linklaw', () => {
			const button = new ButtonView();

			button.label = 'ZakonRada';
			button.tooltip = "Інструмент для створення зв'язків між матеріалом та статтею на Zakon Rada, для подальшої перевірки на актуальність";
			button.withText = true;


			// Show the UI on button click.
			this.listenTo( button, 'execute', () => {
				this._showUI();
			} );

			return button;
		} );
		
		this._enableBalloonActivators();
	}

	_createFormView() {
		const editor = this.editor;
		const formView = new FormView( editor.locale );

		// Execute the command after clicking the "Save" button.
		this.listenTo( formView, 'submit', () => {
			// Grab values from the linklaw and dataLinklaw input fields.
			const value = {
				span: formView.spanInputView.fieldView.element.value,
				dataLinklaw: formView.dataLinklawInputView.fieldView.element.value,
				dataArticlelaw: formView.dataArticlelawInputView.fieldView.element.value,
				dataTooltip: `Посилання: ${formView.dataLinklawInputView.fieldView.element.value}, Стаття: ${formView.dataArticlelawInputView.fieldView.element.value}`,
				dataId: formView.dataIdInputView.fieldView.element.value
			};
			editor.execute( 'addLinklaw', value );

            // Hide the form view after submit.
			this._hideUI();
		} );

		// Hide the form view after clicking the "Cancel" button.
		this.listenTo( formView, 'cancel', () => {
			this._hideUI();
		} );

		this.listenTo( formView, 'delete', () => {
			let dataLinklaw = formView.dataLinklawInputView.fieldView.element.value;
			this._remove(dataLinklaw);
			this._hideUI();
		} );

		// Hide the form view when clicking outside the balloon.
		clickOutsideHandler( {
			emitter: formView,
			activator: () => this._balloon.visibleView === formView,
			contextElements: [ this._balloon.view.element ],
			callback: () => this._hideUI()
		} );

		return formView;
	}

	_createUuidv4() {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
		.replace(/[xy]/g, function (c) {
			const r = Math.random() * 16 | 0, 
				v = c == 'x' ? r : (r & 0x3 | 0x8);
			return v.toString(16);
		});
	}

	_showUI() {
		const selection = this.editor.model.document.selection;

		// Check the value of the command.
		const commandValue = this.editor.commands.get( 'addLinklaw' ).value;

		this._balloon.add( {
			view: this.formView,
			position: this._getBalloonPositionData()
		} );

		// Disable the input when the selection is not collapsed.
		this.formView.spanInputView.isEnabled = false;
		this.formView.dataIdInputView.isEnabled = false;

		// Fill the form using the state (value) of the command.
		if ( commandValue ) {
			this.formView.spanInputView.fieldView.value = commandValue.span;
			this.formView.dataLinklawInputView.fieldView.value = commandValue.dataLinklaw;
			this.formView.dataArticlelawInputView.fieldView.value = commandValue.dataArticlelaw;
			this.formView.dataIdInputView.fieldView.value = commandValue.dataId;
		}
		// If the command has no value, put the currently selected text (not collapsed)
		// in the first field and empty the second in that case.
		else {
			const selectedText = getRangeText( selection.getFirstRange() );

			this.formView.spanInputView.fieldView.value = selectedText;
			this.formView.dataLinklawInputView.fieldView.value = '';
			this.formView.dataArticlelawInputView.fieldView.value = '';
			this.formView.dataIdInputView.fieldView.value = this._createUuidv4();
			
		}

		this.formView.focus();
	}

	_hideUI() {
		// Clear the input field values and reset the form.
		this.formView.spanInputView.fieldView.value = '';
		this.formView.dataLinklawInputView.fieldView.value = '';
		this.formView.dataArticlelawInputView.fieldView.value = '';
		this.formView.dataIdInputView.fieldView.value = '';
		this.formView.element.reset();

		this._balloon.remove( this.formView );

		// Focus the editing view after inserting the linklaw so the user can start typing the content
		// right away and keep the editor focused.
		this.editor.editing.view.focus();
	}

	_getBalloonPositionData() {
		const view = this.editor.editing.view;
		const viewDocument = view.document;
		let target = null;

		// Set a target position by converting view selection range to DOM
		target = () => view.domConverter.viewRangeToDom( viewDocument.selection.getFirstRange() );

		return {
			target
		};
	}

	_enableBalloonActivators() {
		const editor = this.editor;
		const editingView = editor.editing.view;
		editingView.addObserver(ClickObserver);
		const containers = document.querySelectorAll('.ckeditor__container');
		containers.forEach( (box) => {
			box.addEventListener('contextmenu', (event) => {
				event.preventDefault();
				const commandValue = this.editor.commands.get('addLinklaw').value;
				if (commandValue) {
					this._showUI();
				}
			});
		})
	}
	

	_remove(dataLinklaw) {
		const model = this.editor.model;
		const selection = model.document.selection;

		model.change( writer => {
			// Get ranges to unlink.
			const rangesToUnlink = selection.isCollapsed ?
				[ findAttributeRange(
					selection.getFirstPosition(),
					'linklaw',
					dataLinklaw,
					model
				) ] :
				model.schema.getValidRanges( selection.getRanges(), 'linklaw' );

			// Remove `linkHref` attribute from specified ranges.
			for ( const range of rangesToUnlink ) {
				writer.removeAttribute( 'linklaw', range );
				writer.removeAttribute( 'articlelaw', range );
				writer.removeAttribute( 'tooltip', range );
				writer.removeAttribute( 'id', range );
			}
		} );
    }
	
}